import { ref, onBeforeUnmount } from 'vue';

/**
 * Composable for managing AbortController instances with automatic cleanup
 * @returns {Object} Object containing abort controller management functions
 */
export function useAbortController() {
  const controllers = ref(new Map());

  /**
   * Creates a new AbortController for the given key, aborting any existing one
   * @param {string} key - Unique identifier for the abort controller
   * @returns {AbortController} The newly created AbortController
   */
  const create = key => {
    // Abort existing controller if present
    const existing = controllers.value.get(key);
    if (existing) {
      existing.abort();
    }

    // Create and store new controller
    const controller = new AbortController();
    controllers.value.set(key, controller);
    return controller;
  };

  /**
   * Gets the signal for a specific abort controller
   * @param {string} key - Unique identifier for the abort controller
   * @returns {AbortSignal|null} The abort signal or null if not found
   */
  const getSignal = key => {
    const controller = controllers.value.get(key);
    return controller?.signal || null;
  };

  /**
   * Aborts a specific controller by key
   * @param {string} key - Unique identifier for the abort controller
   */
  const abort = key => {
    const controller = controllers.value.get(key);
    if (controller) {
      controller.abort();
      controllers.value.delete(key);
    }
  };

  /**
   * Aborts all active controllers
   */
  const abortAll = () => {
    controllers.value.forEach(controller => {
      controller.abort();
    });
    controllers.value.clear();
  };

  // Cleanup on component unmount
  onBeforeUnmount(() => {
    abortAll();
  });
  return {
    create,
    getSignal,
    abort,
    abortAll
  };
}